/**
 * Configuration for pre-filling reserved form fields with existing player/team official data
 *
 * This file centralizes the mapping between form field names and selectedPlayer properties,
 * making it easy to update if section names or field names change in the future.
 */

export interface ReservedField {
  /** The field name as it appears in the form definition */
  fieldName: string
  /** The property key in the selectedPlayer object */
  playerKey: string
}

// Section names - easy to change if CMS/DB labels change
export const TEAM_OFFICIALS_SECTION = 'Team Official'
export const PLAYER_INFO_SECTION = 'Player Info'

/**
 * Whether a form item is the reserved field a mapping describes.
 *
 * Matching is case-insensitive and ignores an entity-name prefix, because the
 * SAME logical field arrives under more than one name: a team-official form may
 * send either `First Name` or `Team Official First Name`, and both are in use.
 * Strict equality matched only the bare form, so a form using the qualified
 * names prefilled nothing at all.
 *
 * Suffix rather than substring: anchoring at the END keeps `First Name` from
 * also matching a hypothetical `First Name Of Guardian`, while still accepting
 * any `<entity> First Name`. `DynamicForm` solves the same problem with a bare
 * `includes`; this is the narrower version of that idea.
 *
 * A mapping whose own name is longer than the item's can never match, so the
 * qualified mappings (`Player First Name`) stay as specific as they were.
 */
export const matchesReservedField = (
  itemName: string | null | undefined,
  mappingFieldName: string
): boolean => {
  const item = (itemName ?? '').trim().toLowerCase()
  const mapping = mappingFieldName.trim().toLowerCase()
  if (!item || !mapping) {
    return false
  }
  if (item === mapping) {
    return true
  }
  // `Team Official First Name` ends with ` first name`; the leading space keeps
  // this from matching a word that merely ends in the mapping's letters.
  return item.endsWith(` ${mapping}`)
}

// Team Officials form field mappings
export const TEAM_OFFICIAL_FIELDS: ReservedField[] = [
  { fieldName: 'First Name', playerKey: 'name_first' },
  { fieldName: 'Last Name', playerKey: 'name_last' },
  { fieldName: 'Email', playerKey: 'email' },
  { fieldName: 'Birth Date', playerKey: 'birth_date' },
]

// Player form field mappings
export const PLAYER_INFO_FIELDS: ReservedField[] = [
  { fieldName: 'Player First Name', playerKey: 'name_first' },
  { fieldName: 'Player Last Name', playerKey: 'name_last' },
  { fieldName: 'Player Email', playerKey: 'email' },
  { fieldName: 'Player Birth Date', playerKey: 'birth_date' },
  { fieldName: 'Player Position', playerKey: 'player_type_id' },
  {
    fieldName: 'Preferred Jersey Number',
    playerKey: 'preferred_jersey_number',
  },
  {
    fieldName: 'Other Membership Number',
    playerKey: 'other_membership_number',
  },
  {
    fieldName: 'Player Number',
    playerKey: 'player_number',
  },
]
